import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Plus,
  Loader2,
  Shield,
  UserCog,
  MoreHorizontal,
  Mail,
  Calendar,
  Power,
  PowerOff,
  KeyRound,
  Check,
  Info,
  Eye,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

const AdminsList = () => {
  const { api, admin: currentAdmin, impersonate } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // New admin form
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'support'
  });

  // Password reset dialog state
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Role change dialog state
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [changingRole, setChangingRole] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/api/super-admin/admins');
      setAdmins(res.data);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!formData.email || !formData.full_name) {
      setError('Name and email are required');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const res = await api.post('/api/super-admin/auth/create-admin', {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role
      });
      setShowAddDialog(false);
      setFormData({ email: '', full_name: '', role: 'support' });
      fetchAdmins();
      toast.success(res.data.email_sent 
        ? `Invitation sent to ${formData.email}` 
        : 'Agent created but invitation email failed'
      );
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create agent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (adminId, currentStatus) => {
    try {
      await api.put(`/api/super-admin/admins/${adminId}`, {
        is_active: !currentStatus
      });
      fetchAdmins();
      toast.success(`Agent ${currentStatus ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      console.error('Failed to update admin:', err);
      toast.error(err.response?.data?.detail || 'Failed to update agent');
    }
  };

  const handleChangeRole = async () => {
    if (!roleChangeTarget || !newRole) return;
    
    setChangingRole(true);
    try {
      await api.put(`/api/super-admin/admins/${roleChangeTarget.id}`, {
        role: newRole
      });
      toast.success(`Role changed to ${newRole} successfully`);
      setShowRoleDialog(false);
      setRoleChangeTarget(null);
      setNewRole('');
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    } finally {
      setChangingRole(false);
    }
  };

  const openRoleDialog = (admin) => {
    setRoleChangeTarget(admin);
    setNewRole(admin.role);
    setShowRoleDialog(true);
  };

  const handleImpersonate = async (agent) => {
    try {
      await impersonate(agent.id);
      toast.success(`Now viewing as ${agent.full_name} (${agent.role})`);
      navigate('/super-admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to impersonate agent');
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    
    setResetting(true);
    try {
      const res = await api.post(`/api/super-admin/auth/reset-admin-password/${resetTarget.id}`);
      setResetResult(res.data);
      toast.success(res.data.email_sent ? 'Reset link sent' : 'Reset created but email failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
      setShowResetDialog(false);
    } finally {
      setResetting(false);
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeResetDialog = () => {
    setShowResetDialog(false);
    setResetTarget(null);
    setResetResult(null);
    setCopied(false);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-[#ffd108] text-black">Admin</Badge>;
      case 'manager':
        return <Badge className="bg-blue-500 text-white">Manager</Badge>;
      default:
        return <Badge variant="secondary">Support</Badge>;
    }
  };

  const canManageAdmins = currentAdmin?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!canManageAdmins) {
    return (
      <div className="text-center py-12">
        <Shield className={`h-12 w-12 mx-auto ${isDark ? 'text-slate-500' : 'text-gray-400'} mb-4`} />
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Access Restricted</h2>
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Only administrators can manage support agents.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Support Agents</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{admins.length} team member{admins.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
          <Plus className="mr-2 h-4 w-4" /> Add Agent
        </Button>
      </div>

      {/* Role Legend */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Badge className="bg-[#ffd108] text-black">Admin</Badge>
              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Full access, can manage agents</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-500 text-white">Manager</Badge>
              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Can manage tenants & tickets</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Support</Badge>
              <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Can view & respond to tickets</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admins Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Name</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Email</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Role</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Last Login</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((admin) => (
                <TableRow key={admin.id} className={isDark ? 'border-slate-700' : 'border-gray-100'}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${isDark ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                        <UserCog className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{admin.full_name}</p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>@{admin.username}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                      <Mail className={`h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                      {admin.email}
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(admin.role)}</TableCell>
                  <TableCell>
                    {admin.is_active !== false ? (
                      <Badge className={isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}>Active</Badge>
                    ) : (
                      <Badge className={isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}>Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {admin.last_login
                      ? formatDistanceToNow(new Date(admin.last_login), { addSuffix: true })
                      : 'Never'}
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {admin.created_at ? format(new Date(admin.created_at), 'PP') : 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {admin.id !== currentAdmin?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canManageAdmins && admin.role !== 'admin' && admin.is_active !== false && (
                            <DropdownMenuItem
                              onClick={() => handleImpersonate(admin)}
                              data-testid={`impersonate-agent-${admin.id}`}
                            >
                              <Eye className="mr-2 h-4 w-4" /> Impersonate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => openRoleDialog(admin)}
                          >
                            <Shield className="mr-2 h-4 w-4" /> Change Role
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setResetTarget(admin);
                              setShowResetDialog(true);
                            }}
                          >
                            <KeyRound className="mr-2 h-4 w-4" /> Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleActive(admin.id, admin.is_active !== false)}
                          >
                            {admin.is_active !== false ? (
                              <><PowerOff className="mr-2 h-4 w-4" /> Deactivate</>
                            ) : (
                              <><Power className="mr-2 h-4 w-4" /> Activate</>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Invite Support Agent</DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              Invite a new team member to the EQUIPR CORE admin portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Full Name</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                placeholder="John Smith"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@equipr.com"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Role</Label>
              <Select
                value={formData.role}
                onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support Agent</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                {formData.role === 'admin' && 'Full access to all features including agent management'}
                {formData.role === 'manager' && 'Can manage tenants and handle support tickets'}
                {formData.role === 'support' && 'Can view tenants and respond to support tickets'}
              </p>
            </div>

            <div className={`flex items-start gap-2 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'} mt-2`}>
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <p>An invitation email will be sent with a link to set their password.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={handleAddAdmin}
              disabled={submitting}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Send Invite'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={closeResetDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              {resetResult ? 'Reset Link Sent' : 'Reset Password'}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              {resetResult 
                ? `A password reset link has been sent to ${resetResult.admin_name}.`
                : `Send a password reset link to ${resetTarget?.full_name}. They will receive an email with a link to set a new password.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {resetResult ? (
            <div className="py-4 space-y-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${resetResult.email_sent ? 'bg-green-500/20' : 'bg-amber-500/20'}`}>
                    {resetResult.email_sent 
                      ? <Check className="h-5 w-5 text-green-500" />
                      : <KeyRound className="h-5 w-5 text-amber-500" />
                    }
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {resetResult.email_sent ? 'Email sent successfully' : 'Email delivery failed'}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {resetResult.admin_email}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={`flex items-start gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <KeyRound className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>{resetResult.note}</p>
              </div>
            </div>
          ) : (
            <div className="py-4">
              <div className={`flex items-start gap-2 text-sm ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
                <KeyRound className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>This will send a password reset email to <strong>{resetTarget?.email}</strong>. Their current password will remain valid until they complete the reset. The link expires in 15 minutes.</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {resetResult ? (
              <Button onClick={closeResetDialog} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
                Done
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={closeResetDialog}>Cancel</Button>
                <Button
                  onClick={handleResetPassword}
                  disabled={resetting}
                  className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                >
                  {resetting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              Change Role
            </DialogTitle>
            <DialogDescription>
              Change the role for {roleChangeTarget?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Current Role</Label>
              <div className="flex items-center gap-2">
                {getRoleBadge(roleChangeTarget?.role)}
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  ({roleChangeTarget?.email})
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}>
                  <SelectValue placeholder="Select new role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support - Basic ticket handling</SelectItem>
                  <SelectItem value="manager">Manager - Can assign tickets</SelectItem>
                  <SelectItem value="admin">Admin - Full access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              <p><strong>Support:</strong> Can view and respond to tickets</p>
              <p><strong>Manager:</strong> Can assign tickets and view reports</p>
              <p><strong>Admin:</strong> Full access including user management</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="ghost" 
              onClick={() => {
                setShowRoleDialog(false);
                setRoleChangeTarget(null);
                setNewRole('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeRole}
              disabled={changingRole || newRole === roleChangeTarget?.role}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            >
              {changingRole ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Change Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminsList;
